import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

export interface DateRangesOverlapError {
	indices: number[];
}

function toDateOnly(value: unknown): number | null {
	if (value == null) return null;
	const date = value instanceof Date ? value : new Date(value as string);
	if (Number.isNaN(date.getTime())) return null;
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d.getTime();
}

function getRange(group: FormGroup): { start: number; end: number } | null {
	const start = toDateOnly(group.get('start')?.value);
	const end = toDateOnly(group.get('end')?.value);
	if (start == null || end == null) return null;
	if (start >= end) return null;
	return { start, end };
}

function rangesOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
	return a.start < b.end && b.start < a.end;
}

function patchControlError(ctrl: AbstractControl | null, key: string, present: boolean): void {
	if (!ctrl) return;

	const errors = ctrl.errors ?? {};
	const hasKey = !!errors[key];

	if (present && !hasKey) {
		ctrl.setErrors({ ...errors, [key]: true });
	} else if (!present && hasKey) {
		const next = { ...errors };
		delete next[key];
		ctrl.setErrors(Object.keys(next).length ? next : null);
	}
}

export function dateRangesOverlapValidator(): ValidatorFn {
	const KEY = 'dateRangesOverlap';

	return (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormArray)) return null;

		const groups = control.controls.filter((c): c is FormGroup => c instanceof FormGroup);
		if (groups.length === 0) return null;

		const ranges = groups.map((g) => getRange(g));
		const overlapIdx = new Set<number>();

		for (let i = 0; i < ranges.length; i++) {
			const a = ranges[i];
			if (!a) continue;
			for (let j = i + 1; j < ranges.length; j++) {
				const b = ranges[j];
				if (!b) continue;
				if (rangesOverlap(a, b)) {
					overlapIdx.add(i);
					overlapIdx.add(j);
				}
			}
		}

		for (let i = 0; i < groups.length; i++) {
			const g = groups[i];
			const hasOverlap = overlapIdx.has(i);
			patchControlError(g.get('start'), KEY, hasOverlap);
			patchControlError(g.get('end'), KEY, hasOverlap);
		}

		if (overlapIdx.size === 0) return null;

		return {
			[KEY]: { indices: Array.from(overlapIdx).sort((a, b) => a - b) } as DateRangesOverlapError,
		};
	};
}
